import {env, sentryDsn} from "@bounce/shared/constants";
import * as Sentry from "@sentry/react";
import {setTag} from "@sentry/react";
import React, {PropsWithChildren, useCallback, useEffect, createContext} from "react";
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from "react-router-dom";
import {getUrlInfo} from "../routes/urls";
import {useAuthContext} from "./AuthContext";

export interface SentryContextProps {
  setTag: typeof setTag;
}

const SentryContext = createContext<SentryContextProps>(undefined!);

export const SentryProvider = ({children}: PropsWithChildren) => {
  const {pathname} = useLocation();
  const {email} = useAuthContext();
  const enabled = ["production", "staging"].includes(env);

  useEffect(() => {
    if (enabled) {
      Sentry.init({
        enabled: true,
        dsn: sentryDsn,
        environment: env,
        integrations: [
          Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        tracesSampleRate: 1,
        replaysSessionSampleRate: 1,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  }, [enabled]);

  const identifyUser = useCallback((userId?: string): void => {
    if (userId) {
      Sentry.setUser({id: userId});
    } else {
      Sentry.setUser({});
    }
  }, []);

  useEffect(() => {
    if (email) {
      identifyUser(email);
    }
  }, [email]);

  useEffect(() => {
    if (!pathname) {
      return;
    }

    const urlInfo = getUrlInfo(pathname);
    if (!urlInfo) {
      return;
    }
  }, [pathname]);

  return <SentryContext.Provider value={{setTag}}>{children}</SentryContext.Provider>;
};
